import type { DateTime } from "luxon";
import { formatPersonFullName } from "./formatters";
import type { ActualNurse, Nurse, NurseInShift, WeekData } from "./models";
import { snakeToCamel } from "./utilFunctions";

// Only the shift rows arrive snake_case; the day's own fields (census_low, census_override, ...) are
// read snake_case everywhere, so a deep key conversion would break them.
const camelCaseKeys = <T extends object>(row: T): T => {
	const converted: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(row)) {
		converted[snakeToCamel(key)] = value;
	}
	// The backend sends the same fields under snake_case names; after renaming they are the typed shape.
	return converted as T;
};

/** `department_data`-shaped weeks as the backend sends them → the shape the schedule UI reads. */
export const camelCaseWeekShifts = (weeks: WeekData[]): WeekData[] =>
	weeks.map((week) => ({
		...week,
		weeklyShifts: week.weeklyShifts.map((shift) => ({
			...shift,
			days: shift.days.map((day) => ({
				...day,
				nurses: day.nurses?.map(camelCaseKeys),
				nursesActual: day.nursesActual?.map(camelCaseKeys),
			})),
		})),
	}));

/** Scheduled plus punched shift rows across `weeks`; the size of a schedule load. */
export const countShiftRows = (weeks: WeekData[]): number =>
	weeks
		.flatMap((week) => week.weeklyShifts)
		.flatMap((shift) => shift.days)
		// DEFAULT: days before actuals were recorded carry no `nursesActual` list at all, which means no punches.
		.reduce((total, day) => total + day.nurses.length + (day.nursesActual?.length ?? 0), 0);

/** Fills the display fields the backend leaves to the client (name, role, employment type, job name). */
export const applyNurseDetails = (shift: NurseInShift | ActualNurse, nurse: Nurse, lastNameFirst: boolean): void => {
	shift.role = nurse.role;
	shift.name = formatPersonFullName(nurse, lastNameFirst);
	shift.type = nurse.employmentType;
	if (shift.jobId && nurse.jobs?.length) {
		const job = nurse.jobs.find((j) => j.id === shift.jobId);
		if (job) shift.jobName = job.displayName;
	}
};

const withDetails = <T extends NurseInShift | ActualNurse>(shift: T, nurse: Nurse, lastNameFirst: boolean): T => {
	const copy = { ...shift };
	applyNurseDetails(copy, nurse, lastNameFirst);
	return copy;
};

/** One nurse's weeks with that nurse's display fields filled; copies, so cached weeks stay untouched. */
export const withNurseDetails = (weeks: WeekData[], nurse: Nurse, lastNameFirst: boolean): WeekData[] =>
	weeks.map((week) => ({
		...week,
		weeklyShifts: week.weeklyShifts.map((shift) => ({
			...shift,
			days: shift.days.map((day) => ({
				...day,
				nurses: day.nurses.map((row) => withDetails(row, nurse, lastNameFirst)),
				nursesActual: day.nursesActual?.map((row) => withDetails(row, nurse, lastNameFirst)),
			})),
		})),
	}));

/** The nurse's shifts overlapping `timeFrameStart`..`timeFrameEnd` across `weeks`, earliest first; shifts
 * pending removal are left out. An open end means no bound on that side. */
export const nurseShiftsInTimeFrame = (
	weeks: WeekData[],
	nurseId: number,
	timeFrameStart: DateTime | undefined,
	timeFrameEnd: DateTime | undefined,
	isActual: boolean,
): NurseInShift[] =>
	weeks
		.flatMap((week) => week.weeklyShifts)
		.flatMap((shift) => shift.days)
		// Days before actuals were recorded carry no `nursesActual` list at all.
		.flatMap((day) => (isActual ? (day.nursesActual ?? []) : day.nurses))
		.filter(
			(n) =>
				n.nurseId === nurseId &&
				!n.isPendingToDelete &&
				(timeFrameStart === undefined || n.endTime > timeFrameStart) &&
				(timeFrameEnd === undefined || n.startTime < timeFrameEnd),
		)
		.sort((n1, n2) => n1.startTime.toMillis() - n2.startTime.toMillis());
